import React, { useState, useEffect } from 'react';
import { Button, message, Spin } from 'antd';
import { FileExcelOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { getDoctorsReportById } from "../../services/reports/reportsService";
import * as XLSX from 'xlsx';
import { useParams, useLocation } from 'react-router-dom';
import { DataTable, PageCard, PageShell, PageToolbar } from "../../components/ui";

const DoctorsReportDetail = () => {
    const { t } = useTranslation();
    const sum = (value) => `${value} ${t('common.currency')}`;
    const { doctorId } = useParams();
    const location = useLocation();
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            const searchParams = new URLSearchParams(location.search);
            const start_at = searchParams.get('start_at') || '';
            const end_at = searchParams.get('end_at') || '';

            try {
                const data = await getDoctorsReportById(doctorId, start_at, end_at);
                const dataWithKey = data.map((item, index) => ({ ...item, key: index + 1 }));
                setReportData(dataWithKey);
            } catch (error) {
                console.error('Ошибка при получении данных о враче:', error);
                message.error(t('reports.loadError'));
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [doctorId, location.search]);

    const exportToExcel = () => {
        if (reportData.length === 0) {
            message.warning(t('reports.noExport'));
            return;
        }

        const worksheet = XLSX.utils.json_to_sheet(reportData.map(({ key, ...rest }) => rest));
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Doctor Report");
        XLSX.writeFile(workbook, `Doctor_${doctorId}_Report.xlsx`);
    };

    const columns = [
        {
            title: t('patients.colId'),
            dataIndex: 'key',
            key: 'key',
            width: 72,
            sorter: (a, b) => a.key - b.key,
            render: (text) => <span className="text-xs text-slate-500 tabular-nums">{text}</span>,
        },
        {
            title: t('reports.patientName'),
            dataIndex: ['patient_id', 'name'],
            key: 'patientName',
            ellipsis: true,
            sorter: (a, b) => a.patientName.localeCompare(b.patientName),
            render: (text) => <span className="text-sm font-medium text-[#1D2C7B]">{text}</span>,
        },
        {
            title: t('common.amount'),
            dataIndex: 'total_amount',
            key: 'total_amount',
            width: 180,
            align: 'right',
            render: (text) => (
                <span className="text-sm font-medium text-slate-900 tabular-nums whitespace-nowrap">{sum(text)}</span>
            ),
            sorter: (a, b) => a.total_amount - b.total_amount,
        }
    ];

    const renderMobileCard = (record) => (
        <div>
            <span className="block truncate text-sm font-semibold text-[#1D2C7B]">
                {record.patient_id?.name}
            </span>
            <div className="mt-2 flex items-end justify-between gap-2">
                <div className="min-w-0">
                    <span className="block text-[11px] leading-tight text-slate-500">{t('common.amount')}</span>
                    <span className="block text-sm font-medium text-slate-900 tabular-nums">
                        {sum(record.total_amount)}
                    </span>
                </div>
                <span className="shrink-0 text-xs text-slate-400 tabular-nums">№ {record.key}</span>
            </div>
        </div>
    );

    return (
        <PageShell>
            <PageCard>
                <PageToolbar
                    title={t('reports.doctorDetail')}
                    count={reportData.length}
                    actions={
                        <Button
                            type="default"
                            className="!h-9 !rounded-lg !inline-flex !items-center !gap-1.5"
                            icon={<FileExcelOutlined />}
                            onClick={exportToExcel}
                            disabled={reportData.length === 0}
                        >
                            <span className="hidden sm:inline">{t('reports.exportExcel')}</span>
                        </Button>
                    }
                />

                <Spin spinning={loading}>
                    <DataTable
                        dataSource={reportData}
                        columns={columns}
                        mobileCard={renderMobileCard}
                        scrollX={640}
                        rowKey="key"
                        pagination={{
                            showSizeChanger: false,
                            position: ['bottomCenter'],
                            className: 'px-4 pb-3',
                        }}
                    />
                </Spin>
            </PageCard>
        </PageShell>
    );
};

export default DoctorsReportDetail;
